import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { IdPropType } from '../../../proptypes/basic';
import DisplayDate from '../../date/components/DisplayDate';
import Note from '../../notes/components/Note';
import {EXTRA_COST_RESOURCE} from '../../api/resources';
import { useDialog } from '../../dialogs/components/DialogContext';
import { destroyExtraCost } from '../ExtraCostsSlice';
import { useCanDelete, useCanWrite, useCanCreate } from '../../abilities/hooks';
import CreateResourceIconButton from '../../table/components/resource/CreateResourceIconButton';
import ResourceTable from '../../table/components/resource/ResourceTable';
import { useResourceDialog } from '../../dialogs/hooks';
import Card from '@material-ui/core/Card';
import { CardHeader } from '@material-ui/core';
import CardContent from '@material-ui/core/CardContent';
import Employee from '../../employees/components/Employee';
import Agency from '../../agencies/components/Agency';
import Currency from '../../currencies/components/Currency';

const initialOrderBy = [['billedAt', 'desc']];

const columns = [
    {
        key: 'agencyId',
        label: 'Agentur',
        component: Agency,
    },
    {
        key: 'billedAt',
        label: 'Datum',
        as: 'value',
        component: DisplayDate,
    },
    {
        key: 'fullAmount',
        label: 'Betrag gesamt',
        as: 'value',
        component: Currency,
    },
    {
        key: 'shareLocation',
        label: 'Anteil Standort',
        as: 'value',
        component: Currency,
    },
    {
        key: 'note',
        as: 'value',
        label: 'Kommentar',
        component: Note,
    },
    {
        key: 'employeeId',
        label: 'Kundenbetreuer',
        component: Employee,
        withoutFallback: true,
    },
];

const ExtraCosts = ({customerId, disabled }) => {
    const canDeleteExtraCost = useCanDelete(EXTRA_COST_RESOURCE);
    const canCreateExtraCost = useCanCreate(EXTRA_COST_RESOURCE);
    const canWriteExtraCost = useCanWrite(EXTRA_COST_RESOURCE);

    const dispatch = useDispatch();
    const listId = `customer.${customerId}.ExtraCosts`;
    const { openExtraCostDialog } = useDialog();

    const handleCreate = useResourceDialog(EXTRA_COST_RESOURCE, { customerId });

    const handleEditDisabled = useCallback(
        () => disabled || !canWriteExtraCost,
        [disabled, canWriteExtraCost]
    );
    const handleDeleteDisabled = useCallback(
        () => disabled || !canDeleteExtraCost,
        [disabled, canDeleteExtraCost]
    );

    const handleEdit = useCallback(
        (extraCostId) => openExtraCostDialog({ customerId, extraCostId }),
        [customerId, openExtraCostDialog]
    );

    const handleDelete = useCallback(
        (extraCostId) => dispatch(destroyExtraCost({ id: extraCostId })),
        [dispatch]
    );
    const fetchParams = useMemo(() => ({ limit: 5, customerId }), [customerId]);

    return (
        <Card data-cy="card-extraCosts" variant="outlined">
            <CardHeader
                title="Extrakosten"
                action={
                    <CreateResourceIconButton
                        listId={listId}
                        resource={EXTRA_COST_RESOURCE}
                        onClick={handleCreate}
                        color="primary"
                    />
                }
            />
            <CardContent>
                <ResourceTable
                    resource={EXTRA_COST_RESOURCE}
                    columns={columns}
                    listId={listId}
                    onDelete={canDeleteExtraCost?handleDelete:null}
                    fetchParams={fetchParams}
                    initialOrderBy={initialOrderBy}
                    onEdit={handleEdit}
                    editDisabled={handleEditDisabled}
                    deleteDisabled={handleDeleteDisabled}
                    addingDisabled={disabled || !canCreateExtraCost}
                    autoload
                />
            </CardContent>
        </Card>
    );
};

ExtraCosts.propTypes = {
    customerId: IdPropType.isRequired,
};

ExtraCosts.defaultProps = {};

export default ExtraCosts;